import React from 'react';
import { 
  Database, Clock, ChevronRight, Activity, CheckCircle, AlertTriangle, XCircle
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ReplicaCard = ({ replica, projectId, databaseId }) => {
  const navigate = useNavigate();
  const { DBInstanceIdentifier, DBInstanceStatus, Engine, EngineVersion, DBInstanceClass, CreatedTime } = replica;
  
  // Get status configuration
  const getStatusConfig = (status) => {
    switch (status?.toLowerCase()) {
      case 'available':
        return {
          icon: <CheckCircle className="w-4 h-4" />,
          bgColor: 'bg-green-100',
          textColor: 'text-green-800',
          borderColor: 'border-green-200',
          label: 'Active'
        };
      case 'creating':
      case 'modifying':
        return {
          icon: <AlertTriangle className="w-4 h-4" />,
          bgColor: 'bg-yellow-100',
          textColor: 'text-yellow-800',
          borderColor: 'border-yellow-200',
          label: status.charAt(0).toUpperCase() + status.slice(1)
        };
      case 'failed':
      case 'deleting':
      case 'stopped':
        return {
          icon: <XCircle className="w-4 h-4" />,
          bgColor: 'bg-red-100',
          textColor: 'text-red-800',
          borderColor: 'border-red-200',
          label: status.charAt(0).toUpperCase() + status.slice(1)
        };
      default:
        return {
          icon: <Activity className="w-4 h-4" />,
          bgColor: 'bg-gray-100',
          textColor: 'text-gray-800',
          borderColor: 'border-gray-200',
          label: status?.charAt(0).toUpperCase() + status?.slice(1) || 'N/A'
        };
    }
  };

  const statusConfig = getStatusConfig(DBInstanceStatus);
  
  const handleClick = () => {
    navigate(`/project/${projectId}/database/${databaseId}/replicas/${DBInstanceIdentifier}`);
  };

  // Format the creation date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <div
      onClick={handleClick}
      className="bg-white rounded-xl shadow-sm hover:shadow-lg border border-gray-200 overflow-hidden cursor-pointer transition-all duration-200 w-full"
    >
      {/* Top status border */}
      <div className={`h-1 w-full ${statusConfig.bgColor}`}></div>

      {/* Card Body */}
      <div className="p-6 space-y-4">
        {/* Header with Icon and Resource Name */}
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-indigo-50 rounded-lg hover:bg-indigo-100 transition-colors">
            <Database className="w-5 h-5 text-indigo-600" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800 break-words whitespace-normal">
            {DBInstanceIdentifier}
          </h3>
        </div>

        {/* Status and Engine Type */}
        <div className="flex items-center space-x-2">
          {/* Status Badge */}
          <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusConfig.bgColor} ${statusConfig.textColor}`}>
            {statusConfig.icon}
            <span className="ml-1">{statusConfig.label}</span>
          </div>

          {/* Engine Badge */}
          <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-50 text-blue-700 truncate">
            <Activity className="w-4 h-4 flex-shrink-0 mr-1" />
            <span className="truncate">Read Replica</span>
          </div>
        </div>


        {/* Created At Time */}
        <div className="text-sm text-gray-600">
          <div className="flex items-center">
            <Clock className="w-4 h-4 mr-2 text-gray-400" />
            <div className="flex flex-col">
              <p>Created: {formatDate(CreatedTime)}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Footer with CTA */}
      <div className="px-6 py-3 bg-gray-50 border-t border-gray-100">
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium text-gray-600">
            View Details
          </span>
          <ChevronRight className="w-5 h-5 text-indigo-600" />
        </div>
      </div>
    </div>
  );
};

export default ReplicaCard;